import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useAsyncEffect } from "@react-hook/async";
import * as Sentry from "@sentry/react";

import { fetchAuthConfig } from "@shared/api";
import { LoadingScreen } from "@shared/components";
import { RETURN_PATH_KEY } from "@shared/constants";
import { AuthProvider } from "@shared/types";
import { makeParamsStr, redirectToExternal, withToaster } from "@shared/utils";

type RedirectParams = {
  authProviderName: string;
};

const getOAuthAuthorizeUrl = (authProvider: AuthProvider): string => {
  const clientId = authProvider.client_id;
  const providerName = authProvider.provider_name;
  const redirectUri = `${window.location.origin}/oauth/callback/${providerName}`;
  const scopes =
    providerName === "gitlab"
      ? ["openid", "email", "profile", "api"]
      : ["openid", "email", "profile"];

  const baseUrl = authProvider.base_url.replace("/authorize", ""); // backwards compat
  if (baseUrl && clientId) {
    const authorizeParams: { [param: string]: string } = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: scopes.join(" "),
    };
    if (providerName.startsWith("okta")) {
      authorizeParams["code_challenge_method"] = "S256";
    } else if (providerName === "github") {
      delete authorizeParams["response_type"];
      delete authorizeParams["scope"];
    }
    const authUri = authProvider.auth_uri || `${baseUrl}/authorize`;
    const url = `${authUri}?${makeParamsStr(authorizeParams)}`;
    return url;
  } else {
    throw new Error(`provider ${providerName} is unknown`);
  }
};

export const LoginOAuthRedirect: React.FC<
  RouteComponentProps<RedirectParams>
> = ({ match }) => {
  const authProviderName = match.params.authProviderName;

  const history = useHistory();

  useAsyncEffect(async () => {
    const authProvider = await withToaster(
      fetchAuthConfig,
      (err) =>
        `Could not load auth config for ${authProviderName}: ${err.message}`
    )(authProviderName);
    try {
      const url = getOAuthAuthorizeUrl(authProvider);
      const currentUrl = new URL(window.location.toString());
      let returnPath = currentUrl.searchParams.get(RETURN_PATH_KEY);
      if (returnPath === null) {
        // if there is no return path, let's send users to the dashboard
        returnPath = "/orgs/-";
      }
      redirectToExternal(url, returnPath);
    } catch (err) {
      Sentry.captureException(err);
      console.error("could not finish user auth: " + err);
      history.push("/fatal-error");
      return;
    }
  }, []);

  return <LoadingScreen text="Logging in…" />;
};
